import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFilterControlClass } from "./AdminFilters";
import {
  deleteSavedListView,
  loadSavedListViews,
  saveListView,
  type SavedListView,
} from "./saved-list-views";

/**
 * Named URL filter presets — plan/35 C + G.
 * Apply select stays on the primary filter row; save/delete behind “Save view…”.
 */
export function SavedListViewsControl(props: {
  listId: string;
  companySlug: string;
  /** Current list query string (filters only). */
  currentQs: string;
  onApplyQs: (qs: string) => void;
  className?: string;
}) {
  const [views, setViews] = useState<SavedListView[]>(() =>
    loadSavedListViews(props.listId, props.companySlug),
  );
  const [selectedId, setSelectedId] = useState("");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  function refresh() {
    setViews(loadSavedListViews(props.listId, props.companySlug));
  }

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        props.className,
      )}
      aria-label="Saved views"
    >
      <select
        className={cn(adminFilterControlClass, "min-w-[9rem]")}
        value={selectedId}
        aria-label="Saved view"
        onChange={(e) => {
          const id = e.target.value;
          setSelectedId(id);
          const v = views.find((x) => x.id === id);
          if (v) props.onApplyQs(v.qs);
        }}
      >
        <option value="">Views…</option>
        {views.map((v) => (
          <option key={v.id} value={v.id}>
            {v.name}
          </option>
        ))}
      </select>
      {!saving ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="min-h-11 text-[var(--color-muted)]"
          onClick={() => {
            setSaving(true);
            if (!name.trim() && selectedId) {
              const v = views.find((x) => x.id === selectedId);
              if (v) setName(v.name);
            }
          }}
        >
          Save view…
        </Button>
      ) : (
        <>
          <input
            className={cn(adminFilterControlClass, "min-w-[8rem]")}
            placeholder="Name this view…"
            value={name}
            autoFocus
            onChange={(e) => setName(e.target.value)}
            aria-label="Saved view name"
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setSaving(false);
                setName("");
              }
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!name.trim()}
            onClick={() => {
              const overwriteId = selectedId || undefined;
              const next = saveListView(props.listId, props.companySlug, {
                name,
                qs: props.currentQs,
                id: overwriteId,
              });
              setViews(next);
              if (!overwriteId && next[0]) setSelectedId(next[0].id);
              setName("");
              setSaving(false);
              refresh();
            }}
          >
            Save
          </Button>
          {selectedId ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                const next = deleteSavedListView(
                  props.listId,
                  props.companySlug,
                  selectedId,
                );
                setViews(next);
                setSelectedId("");
                setName("");
                setSaving(false);
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setSaving(false);
              setName("");
            }}
          >
            Cancel
          </Button>
        </>
      )}
    </div>
  );
}
